import {
  ActivityAvailabilityMode,
  ActivityPaymentModel,
  BookingQuantityMode,
  type ActivityCategory,
  type ActivityCreate,
} from "@/types";
import { rateFromUIPercent } from "@/lib/commissionRate";
import type { GeocodedLocationValue } from "@/lib/geocodedLocation";
import type { SalesAgentAssignment } from "@/lib/salesAgentCommission";
import {
  ACTIVITY_DESCRIPTION_MAX_LENGTH,
  ACTIVITY_NAME_MAX_LENGTH,
  buildItemDescriptionTranslations,
  buildRequiredTranslations,
  findMissingTranslations,
  findTranslationOverMaxLength,
  hasPartialTranslations,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import { SUPPORTED_LOCALES } from "@/lib/locales";

type ActivityFlow = Pick<
  ActivityCreate,
  "booking_quantity_mode" | "availability_mode" | "requires_handshake"
>;

/** Receipt-based activities (restaurants) book by guests and need the seller's handshake. */
export function flowForPaymentModel(
  paymentModel: ActivityPaymentModel,
): ActivityFlow {
  if (paymentModel === ActivityPaymentModel.RECEIPT_BASED) {
    return {
      booking_quantity_mode: BookingQuantityMode.GUESTS,
      availability_mode: ActivityAvailabilityMode.SCHEDULED,
      requires_handshake: true,
    };
  }
  return {
    booking_quantity_mode: BookingQuantityMode.QUANTITY,
    availability_mode: ActivityAvailabilityMode.SCHEDULED,
    requires_handshake: false,
  };
}

export type TranslationCompletenessIssue =
  "name_missing" | "name_incomplete" | "description_partial";

export type TranslationLengthIssue = "name_too_long" | "description_too_long";

export type TranslationIssue =
  TranslationCompletenessIssue | TranslationLengthIssue;

/** Names are required in every language; the description is all-or-nothing. */
export function findTranslationCompletenessIssue(
  names: TranslationFormValues,
  descriptions: TranslationFormValues,
): TranslationCompletenessIssue | null {
  const missingNames = findMissingTranslations(names).length;
  if (missingNames === SUPPORTED_LOCALES.length) return "name_missing";
  if (missingNames > 0) return "name_incomplete";
  if (hasPartialTranslations(descriptions)) return "description_partial";
  return null;
}

/**
 * Re-checked at submit: the inputs cap typing at the max length, but
 * auto-translate can fill a language past it.
 */
export function findTranslationIssue(
  names: TranslationFormValues,
  descriptions: TranslationFormValues,
): TranslationIssue | null {
  const completenessIssue = findTranslationCompletenessIssue(
    names,
    descriptions,
  );
  if (completenessIssue) return completenessIssue;
  if (findTranslationOverMaxLength(names, ACTIVITY_NAME_MAX_LENGTH)) {
    return "name_too_long";
  }
  if (
    findTranslationOverMaxLength(descriptions, ACTIVITY_DESCRIPTION_MAX_LENGTH)
  ) {
    return "description_too_long";
  }
  return null;
}

export interface ActivityCreateDraft {
  category: ActivityCategory;
  paymentModel: ActivityPaymentModel;
  commissionPercent: number;
  nameTranslations: TranslationFormValues;
  descriptionTranslations: TranslationFormValues;
  regionId: string;
  /** `null` when the activity has no fixed location. */
  location: GeocodedLocationValue | null;
  ownerUserId: string;
  salesAgent: SalesAgentAssignment | null;
}

export function buildActivityCreatePayload(
  draft: ActivityCreateDraft,
): ActivityCreate {
  return {
    category: draft.category,
    payment_model: draft.paymentModel,
    ...flowForPaymentModel(draft.paymentModel),
    name_translations: buildRequiredTranslations(draft.nameTranslations),
    region_id: draft.regionId,
    description_translations: buildItemDescriptionTranslations(
      draft.descriptionTranslations,
    ),
    address: draft.location?.address ?? "",
    city: "",
    postal_code: "",
    country: "",
    coordinates: draft.location?.coordinates ?? null,
    commission_rate: rateFromUIPercent(draft.commissionPercent),
    owner_user_id: draft.ownerUserId,
    ...draft.salesAgent,
  };
}
